/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {CodeFilterModel} from '../code-filter/code-filter.model';
import {AbstractFilterComponent} from '../abstract-filter.component';
import {InputTextComponent} from '../../input/input-text/input-text.component';
import {InputAndCodeFilterStateModel} from './input-and-code-filter-state.model';
import {CodeFilterStateModel} from '../code-filter/code-filter-state.model';
import {CodeFilterUtil} from '../code-filter/code-filter.util';
import {ReplaySubject} from 'rxjs';
import {SpinnerService} from '../../spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../spinner/spinner-context-type.enum';

@Component({
    selector: 'app-input-and-code-filter',
    templateUrl: './input-and-code-filter.component.html',
    styleUrls: ['../filter.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
/**
 * Component that allows a user to filter both by user search as well as codes.
 * @todo CTWILS-2590/CTWILS-3156 - This component should extend from both the input and the code filters
 */
export class InputAndCodeFilterComponent extends AbstractFilterComponent<InputAndCodeFilterStateModel> implements OnInit {
    @Input() public placeHolder: string;
    @Input() public codes: CodeFilterModel[] = [];
    @Input() hasEmptyOption = false;
    @Input() dataSubject: ReplaySubject<CodeFilterModel[]>;
    @Output() onOpen = new EventEmitter<void>();
    @ViewChild('searchInput') searchInput: InputTextComponent;

    public spinnerObservable = this.spinnerService.onLocalChange(this.onOpen);

    public allFilter = {
        code: 'ALL',
        name: 'All',
        checked: true,
        indeterminate: false
    } as CodeFilterModel;

    public emptyFilter = {
        code: 'EMPTY',
        name: 'Empty',
        checked: true
    } as CodeFilterModel;

    constructor(public spinnerService: SpinnerService) {
        super();
    }

    ngOnInit(): void {
        this.dataSubject?.subscribe((result) => {
            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.onOpen);
            this.codes = result;
            this.storeState({
                filteredCodes: result,
                all: true
            });
        });
    }

    updateSearchInputFilters(searchText: string): void {
        if (this.codes.length === 0) {
            if (searchText != null && searchText.length > 0) {
                if (this.hasEmptyOption) {
                    CodeFilterUtil.setCodeUnchecked(this.emptyFilter);
                    CodeFilterUtil.setCodeIndeterminate(this.allFilter);
                } else {
                    CodeFilterUtil.setCodeUnchecked(this.allFilter);
                }
            } else {
                if (this.hasEmptyOption) {
                    CodeFilterUtil.setCodeChecked(this.emptyFilter);
                }
                CodeFilterUtil.setCodeChecked(this.allFilter);
            }
        }

        this.storeState({
            searchText,
            empty: this.hasEmptyOption ? this.emptyFilter.checked : null,
            all: this.allFilter.indeterminate ? null : this.allFilter.checked
        });
    }

    resetFilter(): void {
        super.resetFilter();
        this.storeState({
            filteredCodes: this.codes
        });
        // "as unknown as Event" instead of "as Event" prevents TS2352
        this.toggleAllCode({target: {checked: true} as HTMLInputElement} as unknown as Event);
    }

    toggleAllCode(event: Event): void {
        this.searchInput.deleteInputValue();

        this.codes.forEach((code) => {
            this.toggleCode(code, event);
        });

        const isChecked = (event.target as HTMLInputElement).checked;

        if (isChecked) {
            if (this.hasEmptyOption) {
                CodeFilterUtil.setCodeChecked(this.emptyFilter);
            }
            CodeFilterUtil.setCodeChecked(this.allFilter);
        } else {
            if (this.hasEmptyOption) {
                CodeFilterUtil.setCodeUnchecked(this.emptyFilter);
            }
            CodeFilterUtil.setCodeUnchecked(this.allFilter);
        }

        this.storeState({
            empty: this.hasEmptyOption ? this.emptyFilter.checked : null,
            all: this.allFilter.indeterminate ? null : this.allFilter.checked
        });
    }

    toggleEmptyCode(event: Event): void {
        const isChecked = (event.target as HTMLInputElement).checked;

        if (isChecked) {
            CodeFilterUtil.setCodeChecked(this.emptyFilter);
        } else {
            CodeFilterUtil.setCodeUnchecked(this.emptyFilter);
        }

        if (this.state.searchText != null || this.hasEmptyOption) {
            CodeFilterUtil.setCodeIndeterminate(this.allFilter);
        } else if (isChecked) {
            CodeFilterUtil.setCodeChecked(this.allFilter);
        } else {
            CodeFilterUtil.setCodeUnchecked(this.allFilter);
        }

        this.storeState({
            empty: this.hasEmptyOption ? this.emptyFilter.checked : null,
            all: this.allFilter.indeterminate ? null : this.allFilter.checked
        });
    }

    // This function is mutating state which may be difficult to work with state management frameworks in the future.
    toggleCode(code: CodeFilterModel, event: Event): void {
        code.checked = (event.target as HTMLInputElement).checked;

        const codes = this.hasEmptyOption ? [...this.state.filteredCodes, this.emptyFilter] : this.state.filteredCodes;

        if (!codes.find((c) => c.checked === false)) {
            CodeFilterUtil.setCodeChecked(this.allFilter);
        } else if (!codes.find((c) => c.checked === true)) {
            CodeFilterUtil.setCodeUnchecked(this.allFilter);
        } else {
            CodeFilterUtil.setCodeIndeterminate(this.allFilter);
        }

        this.storeState({
            all: this.allFilter.indeterminate ? null : this.allFilter.checked
        });
    }

    emitSearch(): void {
        if (!this.hasEmptyOption && this.state.all === false) {
            const originalSearch = this.state.searchText;
            // "as unknown as Event" instead of "as Event" prevents TS2352
            this.toggleAllCode({target: {checked: true} as HTMLInputElement} as unknown as Event);
            this.searchInput.setValue(originalSearch);
        }

        this.filterEvent.emit(this.state);
    }

    hasFilterScroll(codes: CodeFilterModel[]): boolean {
        if (codes) {
            return codes.length > 0;
        }
        return false;
    }

    storeState(state?: Partial<CodeFilterStateModel>): void {
        super.storeState(state);

        if (!this.allFilter.checked) {
            const codes = this.hasEmptyOption ? [...(this.state.filteredCodes ?? []), this.emptyFilter] : this.state.filteredCodes;
            super.storeState({
                isActive: this.state.searchText !== '' || this.state.searchText != null || codes.some((c) => c.checked)
            });
        } else {
            super.storeState({
                isActive: this.state.searchText != null && this.state.searchText.trim().length > 0 || this.allFilter.indeterminate
            });
        }
    }

    startFilter(): void {
        if (this.state.filteredCodes == null) {
            if (this.dataSubject) {
                // Asynchronous data load
                this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.onOpen);
                this.onOpen.emit();
            } else {
                // Synchronous data load (already passed from parent component)
                this.storeState({
                    filteredCodes: this.codes,
                    all: true
                });
            }
        }
        this.emit(this.dropdown.isOpen());
    }

    initFilter(state: InputAndCodeFilterStateModel): void {
        if (state) {
            if (state.searchText != null) {
                this.searchInput.setValue(state.searchText);
            }

            if (state.empty) {
                CodeFilterUtil.setCodeChecked(this.emptyFilter);
            } else {
                CodeFilterUtil.setCodeUnchecked(this.emptyFilter);
            }

            if (state.all == null) {
                CodeFilterUtil.setCodeIndeterminate(this.allFilter);
            } else if (state.all === true) {
                CodeFilterUtil.setCodeChecked(this.allFilter);
            } else {
                CodeFilterUtil.setCodeUnchecked(this.allFilter);
            }

            if (state.filteredCodes != null) {
                this.codes = state.filteredCodes;
            }

            this.storeState(state);
        }
    }
}
